/**
 * A custom hook that retrieves conversations data categorized by their completion status.
 *
 * This hook makes three separate API calls:
 * - One for completed conversations (COMPLETED filter) with pagination
 * - One for incomplete conversations (INCOMPLETE filter) with pagination
 * - One for all conversations (used for translations) without filtering
 *
 * On initial load (no filters), both completed and incomplete are fetched to get counts.
 * When filters are applied, only the active view is fetched to avoid unnecessary API calls.
 *
 * Pagination state is managed externally via URL parameters.
 */
import { useEffect, useRef } from 'react';
import { useGetAllConversations } from '@/reactQuery/get';
import { TranscriptsView } from '@/enums';
import useConversationsStore from '@/store/ConversationsStore';
import {
  conversationsActionsSelector,
  completedConversationsSelector,
  incompleteConversationsSelector,
} from '@/selectors/conversations';

const PAGE_SIZE = 20;

export const useConversationsByStatus = (
  filterId: string | null,
  language: string | null,
  refetchInterval: number,
  currentProjectIsTranslating: boolean,
  projectId?: string,
  urlCurrentPage: number = 1,
  isCompletedTranscriptsView: boolean = true,
  selectedTranscript: boolean = false,
  conversationFromQuestionsTab?: boolean,
) => {
  // Track initial load with selected transcript
  const isInitialSelectedTranscriptLoad = useRef(selectedTranscript);
  // Preserve counts across re-renders when not fetching both views
  const preservedCompletedCount = useRef<number>(0);
  const preservedIncompleteCount = useRef<number>(0);

  // Store access using selectors
  const {
    setCompletedConversations,
    setIncompleteConversations,
    setCompletedTotalPages,
    setIncompleteTotalPages,
  } = useConversationsStore(conversationsActionsSelector);
  const storeCompleted = useConversationsStore(completedConversationsSelector);
  const storeIncomplete = useConversationsStore(incompleteConversationsSelector);

  // Store state checks
  const hasCompletedInStore = storeCompleted?.length > 0;
  const hasIncompleteInStore = storeIncomplete?.length > 0;

  // View and fetch conditions
  const shouldFetchTranslatedConversations =
    (!!language || currentProjectIsTranslating) && !hasCompletedInStore;

  // Base fetch conditions
  const shouldFetchCompleted = isCompletedTranscriptsView || shouldFetchTranslatedConversations;
  const shouldFetchIncomplete = !shouldFetchTranslatedConversations && !isCompletedTranscriptsView;
  // When both counts are zero
  const countsInStore = preservedCompletedCount.current > 0 && preservedIncompleteCount.current > 0;

  // Only fetch both views for counts
  const shouldFetchBothForCounts =
    (!shouldFetchTranslatedConversations && !filterId && !countsInStore) || !!filterId;

  // Navigation fetch conditions (for single transcript view when store is empty)
  const needsCompletedForNavigation =
    selectedTranscript && isCompletedTranscriptsView && !hasCompletedInStore;
  const needsIncompleteForNavigation =
    selectedTranscript && !isCompletedTranscriptsView && !hasIncompleteInStore;

  const isViewingTranscriptDuringTranslation = selectedTranscript && currentProjectIsTranslating;

  // Final fetch decisions
  const shouldFetchCompletedData =
    (!selectedTranscript && (shouldFetchCompleted || shouldFetchBothForCounts)) ||
    (needsCompletedForNavigation &&
      !conversationFromQuestionsTab &&
      !isViewingTranscriptDuringTranslation);

  const shouldFetchIncompleteData =
    (!selectedTranscript && (shouldFetchIncomplete || shouldFetchBothForCounts)) ||
    needsIncompleteForNavigation;

  // Fetch completed conversations
  const {
    data: { items: completedConversations, pagination: completedPagination } = {},
    isLoading: isLoadingCompleted,
  } = useGetAllConversations(
    projectId,
    filterId,
    shouldFetchCompletedData,
    language,
    refetchInterval,
    TranscriptsView.COMPLETED,
    { page: shouldFetchCompleted ? urlCurrentPage : 1, limit: PAGE_SIZE },
  );

  // Fetch incomplete conversations
  const {
    data: { items: incompleteConversations, pagination: incompletePagination } = {},
    isLoading: isLoadingIncomplete,
  } = useGetAllConversations(
    projectId,
    filterId,
    shouldFetchIncompleteData && !conversationFromQuestionsTab && !currentProjectIsTranslating,
    language,
    refetchInterval,
    TranscriptsView.INCOMPLETE,
    { page: shouldFetchIncomplete ? urlCurrentPage : 1, limit: PAGE_SIZE },
  );

  // Update store when conversations data changes
  useEffect(() => {
    // When page reloaded and we see the single transcript view, we should set store only once
    if (selectedTranscript && !isInitialSelectedTranscriptLoad.current) return;

    if (completedConversations?.length) {
      setCompletedConversations([...completedConversations]);
      isInitialSelectedTranscriptLoad.current = false;
    }
    if (incompleteConversations?.length) {
      setIncompleteConversations([...incompleteConversations]);
      isInitialSelectedTranscriptLoad.current = false;
    }
    // Update total pages in store
    if (completedPagination?.totalPages) {
      setCompletedTotalPages(completedPagination.totalPages);
    }
    if (incompletePagination?.totalPages) {
      setIncompleteTotalPages(incompletePagination.totalPages);
    }
  }, [
    completedConversations,
    incompleteConversations,
    setCompletedConversations,
    setIncompleteConversations,
    selectedTranscript,
    setCompletedTotalPages,
    setIncompleteTotalPages,
    completedPagination?.totalPages,
    incompletePagination?.totalPages,
  ]);

  // Return loading state only for the currently active fetch
  const isLoading = (shouldFetchCompleted && isLoadingCompleted) || isLoadingIncomplete;

  // Update preserved counts when we have new pagination data
  useEffect(() => {
    if (completedPagination?.totalItems) {
      preservedCompletedCount.current = completedPagination.totalItems;
    }
  }, [completedPagination?.totalItems]);

  useEffect(() => {
    if (incompletePagination?.totalItems) {
      preservedIncompleteCount.current = incompletePagination.totalItems;
    }
  }, [incompletePagination?.totalItems]);

  // Calculate counts - use current pagination if available, otherwise use preserved values
  const completedCount = completedPagination?.totalItems ?? preservedCompletedCount.current;
  const incompleteCount = incompletePagination?.totalItems ?? preservedIncompleteCount.current;
  // Calculate total count based on active view
  const totalCount = isCompletedTranscriptsView ? completedCount : incompleteCount;

  return {
    isLoading,
    completed: shouldFetchCompleted ? completedConversations || [] : [],
    incomplete: shouldFetchIncomplete ? incompleteConversations || [] : [],
    completedCount,
    incompleteCount,
    totalCount,
  };
};
